import { css } from '@emotion/react'
import { useState, useEffect, useRef } from 'react'
import type { GetOptionLabelParameters } from '../Dropdown'

import FormLabel from '../FormLabel'
import Dropdown, { DropdownProps, DropdownMode } from '../Dropdown'

const autoContainerCss = css`
  position: relative;
  padding-bottom: 5px;
`

type AutocompleteOption =
  | {
      [key: string]: string
    }
  | undefined

export type AutocompleteProps = {
  /** Id of control */
  id: string
  /** Automatically focus the input when component mounts */
  autoFocus?: boolean
  /** If the input should be cleared when blurred */
  clearInputOnBlur?: boolean
  /** Label for field */
  label: string
  /** Label key of an option */
  labelKey?: string
  /** Custom aria-label for the dropdown menu */
  menuAriaLabel?: string
  /** Show a static option at the bottom of the results */
  showStaticOption?: boolean
  /** Indicates that label should be screen-ready only */
  srOnlyLabel?: boolean
  /** Value key of an option */
  valueKey?: string
} & DropdownProps

/** Note: this is duplicated in scheduler monolith. */
const Autocomplete = ({
  autoFocus = false,
  clearInputOnBlur = true,
  id,
  inputValue,
  label,
  labelKey = 'label',
  menuAriaLabel,
  placeholder,
  showStaticOption = false,
  srOnlyLabel = false,
  value,
  valueKey = 'value',
  ...props
}: AutocompleteProps) => {
  const [focused, setFocused] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const vKey = valueKey || ''
  const lKey = labelKey || ''
  const addItemOption = {
    [vKey]: 'ADD_ITEM_OPTION',
    [lKey]: 'Add your own item'
  }

  const onFocus = () => setFocused(true)
  const onBlur = () => setFocused(false)

  useEffect(() => {
    if (autoFocus && containerRef.current) {
      // Focus the input element within the dropdown
      const inputElement = containerRef.current.querySelector('input')
      if (inputElement) {
        setTimeout(() => {
          inputElement.focus()
        }, 100)
      }
    }
  }, [autoFocus])

  const getOptionLabel = (parameters: GetOptionLabelParameters) => {
    const autoOpt = parameters.option as AutocompleteOption

    if (!autoOpt) {
      return ''
    }

    return showStaticOption && autoOpt[vKey] === addItemOption[vKey]
      ? `Add "${inputValue}"`
      : autoOpt[lKey]
  }

  return (
    <div css={autoContainerCss} ref={containerRef}>
      <FormLabel
        associatedId={id}
        focused={focused}
        shrink={!!inputValue || focused || !!value}
        srOnly={srOnlyLabel || !!(!focused && placeholder)}
      >
        {label}
      </FormLabel>
      <Dropdown
        {...props}
        id={id}
        clearInputOnBlur={clearInputOnBlur}
        clearInputOnSelect
        getOptionLabel={getOptionLabel}
        inputId={id}
        inputValue={inputValue}
        isSearchable
        labelKey={labelKey}
        menuAriaLabel={menuAriaLabel}
        mode={DropdownMode.Simple}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder={placeholder}
        showInput
        srOnlyLabel={srOnlyLabel}
        staticOption={showStaticOption && inputValue ? addItemOption : undefined}
        value={value}
        valueKey={valueKey}
      />
    </div>
  )
}

export default Autocomplete
